import React, {
  useEffect,
  useRef,
  createContext,
  useContext,
  ReactNode,
} from "react";
import Phaser from "phaser";
import { PreloadScene } from "../game/scenes/PreloadScene";

interface CameraContextType {
  camera: Phaser.Cameras.Scene2D.Camera | null;
  scene: Phaser.Scene | null;
}

const CameraContext = createContext<CameraContextType>({
  camera: null,
  scene: null,
});

export const useCameraContext = () => useContext(CameraContext);

interface GameStageProps {
  gameConfig: Phaser.Types.Core.GameConfig;
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const GameStage: React.FC<GameStageProps> = ({
  gameConfig,
  children,
  className,
  style,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);
  const [cameraContext, setCameraContext] = React.useState<CameraContextType>({
    camera: null,
    scene: null,
  });

  // Initialise Phaser only once on mount. Subsequent re-renders will NOT
  // destroy / recreate the Game instance just because `gameConfig` got a new
  // reference (which happens every React render). This prevents the costly
  // asset pre-load screen from flashing after every budget transfer.
  useEffect(() => {
    if (!containerRef.current || gameRef.current) return;

    // Create Phaser game with the provided (initial) config
    const config: Phaser.Types.Core.GameConfig = {
      ...gameConfig,
      parent: containerRef.current,
    };

    gameRef.current = new Phaser.Game(config);

    // Expose game instance globally for hook access
    (window as any).PhaserGame = gameRef.current;

    // Set up camera context when scene is ready
    const setupCameraContext = () => {
      const scenes = gameRef.current?.scene.scenes;
      const scene = scenes?.[0]; // Get first scene
      if (scene && scene.cameras?.main) {
        setCameraContext({
          camera: scene.cameras.main,
          scene: scene,
        });
      }
    };

    // Try to set up immediately, or wait for scene to be ready
    if (gameRef.current.scene.scenes.length > 0) {
      setupCameraContext();
    } else {
      gameRef.current.events.once("ready", setupCameraContext);
    }

    const handleResize = () => {
      if (gameRef.current) {
        gameRef.current.scale.refresh();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
        delete (window as any).PhaserGame;

        // Reset preload flag so next game instance reloads assets
        (PreloadScene as any).loadedOnce = false;
      }
    };
  }, []); // <-- empty dependency ensures single initialisation

  const containerStyle: React.CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    ...style,
  };

  const overlayStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: 1000,
  };

  return (
    <CameraContext.Provider value={cameraContext}>
      <div ref={containerRef} className={className} style={containerStyle}>
        {/* Phaser canvas will be inserted here */}

        {/* React overlay layer */}
        <div style={overlayStyle}>{children}</div>
      </div>
    </CameraContext.Provider>
  );
};
